/**
 * Single source of truth for locale-specific URL path segments per route
 * name (SearchPage -> /search, /recherche, /busqueda, /搜索, etc).
 *
 * Plain CommonJS (module.exports) so this exact object can be require()'d
 * unmodified by BOTH the webpack/babel client bundle (via
 * src/util/urlHelpers.js, which re-exports TRANSLATED_PATHS and
 * getTranslatedPath for ES module consumers) AND the server's untranspiled
 * Node runtime (server/index.js and server/api-util/*), which cannot parse
 * ES `import`/`export` syntax. Do not duplicate this table anywhere else —
 * a hardcoded, hand-copied English-only path list is exactly the bug class
 * this file exists to prevent (see hotfix/maps-broken-non-english-locales
 * and the server/index.js Cache-Control / X-Robots-Tag fixes that shipped
 * alongside it).
 */
const TRANSLATED_PATHS = {
  LandingPage: {
    en: '/',
    es: '/',
    fr: '/',
    zh: '/',
  },
  // The admin console. Deliberately the same path in every language: it is a staff tool, it
  // is never linked from the site and never indexed, so there is nothing for a translated
  // address to earn. Listed here rather than left out so the noindex and private-cache
  // matchers, which derive their prefixes from this table, cover it automatically.
  ConsolePage: {
    en: '/console',
    es: '/console',
    fr: '/console',
    zh: '/console',
  },
  // The console's activity log (P3-3), the first console section with a page of its own. Same
  // path in every language for the same reason as the console itself. Listed here so the
  // noindex and private-cache matchers cover it; they already match anything under /console,
  // and this keeps that true if the console's own prefix ever changes.
  ConsoleActivityLogPage: {
    en: '/console/activity-log',
    es: '/console/activity-log',
    fr: '/console/activity-log',
    zh: '/console/activity-log',
  },
  // The console's members section (P3-4). Same path in every language for the same reason as
  // the console itself. Both the search page and one member's page are listed, so the noindex
  // and private-cache matchers cover them explicitly as well as by the console prefix.
  ConsoleUsersPage: {
    en: '/console/users',
    es: '/console/users',
    fr: '/console/users',
    zh: '/console/users',
  },
  ConsoleUserPage: {
    en: '/console/users/:id',
    es: '/console/users/:id',
    fr: '/console/users/:id',
    zh: '/console/users/:id',
  },
  // The console's listings section (P3-5). Same path in every language for the same reason as
  // the console itself, and listed so the noindex and private-cache matchers cover both the
  // search page and one listing's page explicitly.
  ConsoleListingsPage: {
    en: '/console/listings',
    es: '/console/listings',
    fr: '/console/listings',
    zh: '/console/listings',
  },
  ConsoleListingPage: {
    en: '/console/listings/:id',
    es: '/console/listings/:id',
    fr: '/console/listings/:id',
    zh: '/console/listings/:id',
  },
  CartPage: {
    en: '/cart',
    es: '/carrito',
    fr: '/panier',
    zh: '/购物车',
  },
  CMSPage: {
    en: '/page/:pageId',
    es: '/pagina/:pageId',
    fr: '/page/:pageId',
    zh: '/页面/:pageId',
  },
  SearchPage: {
    en: '/search',
    es: '/busqueda',
    fr: '/recherche',
    zh: '/搜索',
  },
  ListingBasePage: {
    en: '/listing',
    es: '/alquiler',
    fr: '/location',
    zh: '/租赁',
  },
  ListingPage: {
    en: '/rentals/:slug/:id',
    es: '/alquileres/:slug/:id',
    fr: '/locations/:slug/:id',
    zh: '/租赁/:slug/:id',
  },
  ListingPageCityCategory: {
    en: '/rentals/:city/:category/:slug/:id',
    es: '/alquileres/:city/:category/:slug/:id',
    fr: '/locations/:city/:category/:slug/:id',
    zh: '/租赁/:city/:category/:slug/:id',
  },
  ListingPageCity: {
    en: '/rentals/:city/:slug/:id',
    es: '/alquileres/:city/:slug/:id',
    fr: '/locations/:city/:slug/:id',
    zh: '/租赁/:city/:slug/:id',
  },
  ListingPageCategory: {
    en: '/rentals/:category/:slug/:id',
    es: '/alquileres/:category/:slug/:id',
    fr: '/locations/:category/:slug/:id',
    zh: '/租赁/:category/:slug/:id',
  },
  CheckoutPageOld: {
    en: '/l/:slug/:id/checkout',
    es: '/l/:slug/:id/pago',
    fr: '/l/:slug/:id/paiement',
    zh: '/l/:slug/:id/结账',
  },
  CheckoutPage: {
    en: '/listing/:slug/:id/checkout',
    es: '/alquiler/:slug/:id/pago',
    fr: '/location/:slug/:id/paiement',
    zh: '/租赁/:slug/:id/结账',
  },
  ListingPageVariantOld: {
    en: '/l/:slug/:id/:variant',
    es: '/l/:slug/:id/:variant',
    fr: '/l/:slug/:id/:variant',
    zh: '/l/:slug/:id/:variant',
  },
  ListingPageVariant: {
    en: '/listing/:slug/:id/:variant',
    es: '/alquiler/:slug/:id/:variant',
    fr: '/location/:slug/:id/:variant',
    zh: '/租赁/:slug/:id/:variant',
  },
  NewListingPage: {
    en: '/listing/new',
    es: '/alquiler/nuevo',
    fr: '/location/nouveau',
    zh: '/租赁/新建',
  },
  EditListingPage: {
    en: '/listing/:slug/:id/:type/:tab',
    es: '/alquiler/:slug/:id/:type/:tab',
    fr: '/location/:slug/:id/:type/:tab',
    zh: '/租赁/:slug/:id/:type/:tab',
  },
  EditListingStripeOnboardingPage: {
    en: '/listing/:slug/:id/:type/:tab/:returnURLType',
    es: '/alquiler/:slug/:id/:type/:tab/:returnURLType',
    fr: '/location/:slug/:id/:type/:tab/:returnURLType',
    zh: '/租赁/:slug/:id/:type/:tab/:returnURLType',
  },
  ProfileBasePage: {
    en: '/u',
    es: '/u',
    fr: '/u',
    zh: '/u',
  },
  ProfilePage: {
    en: '/rental-owner/:city/:slug/:id',
    es: '/propietario-alquiler/:city/:slug/:id',
    fr: '/proprietaire-location/:city/:slug/:id',
    zh: '/租赁业主/:city/:slug/:id',
  },
  ProfilePageNoCity: {
    en: '/rental-owner/:slug/:id',
    es: '/propietario-alquiler/:slug/:id',
    fr: '/proprietaire-location/:slug/:id',
    zh: '/租赁业主/:slug/:id',
  },
  ProfileSettingsPage: {
    en: '/profile-settings',
    es: '/configuracion-perfil',
    fr: '/parametres-profil',
    zh: '/个人资料设置',
  },
  LoginPage: {
    en: '/login',
    es: '/iniciar-sesion',
    fr: '/connexion',
    zh: '/登录',
  },
  SignupPage: {
    en: '/signup',
    es: '/registrarse',
    fr: '/inscription',
    zh: '/注册',
  },
  ConfirmPage: {
    en: '/confirm',
    es: '/confirmar',
    fr: '/confirmer',
    zh: '/确认',
  },
  PasswordRecoveryPage: {
    en: '/recover-password',
    es: '/recuperar-contrasena',
    fr: '/recuperer-mot-de-passe',
    zh: '/恢复密码',
  },
  InboxBasePage: {
    en: '/inbox',
    es: '/bandeja-de-entrada',
    fr: '/boite-de-reception',
    zh: '/收件箱',
  },
  InboxPage: {
    en: '/inbox/:tab',
    es: '/bandeja-de-entrada/:tab',
    fr: '/boite-de-reception/:tab',
    zh: '/收件箱/:tab',
  },
  OrderDetailsPage: {
    en: '/order/:id',
    es: '/pedido/:id',
    fr: '/commande/:id',
    zh: '/订单/:id',
  },
  OrderDetailsPageRedirect: {
    en: '/order/:id/details',
    es: '/pedido/:id/detalles',
    fr: '/commande/:id/details',
    zh: '/订单/:id/详情',
  },
  SaleDetailsPage: {
    en: '/sale/:id',
    es: '/venta/:id',
    fr: '/vente/:id',
    zh: '/销售/:id',
  },
  SaleDetailsPageRedirect: {
    en: '/sale/:id/details',
    es: '/venta/:id/detalles',
    fr: '/vente/:id/details',
    zh: '/销售/:id/详情',
  },
  ManageListingsPage: {
    en: '/listings',
    es: '/listados',
    fr: '/annonces',
    zh: '/管理列表',
  },
  AccountSettingsPage: {
    en: '/account',
    es: '/cuenta',
    fr: '/compte',
    zh: '/账户',
  },
  ContactDetailsPage: {
    en: '/account/contact-details',
    es: '/cuenta/detalles-contacto',
    fr: '/compte/details-contact',
    zh: '/账户/联系方式',
  },
  NotificationsPage: {
    en: '/account/notifications',
    es: '/cuenta/notificaciones',
    fr: '/compte/notifications',
    zh: '/账户/通知',
  },
  PasswordChangePage: {
    en: '/account/change-password',
    es: '/cuenta/cambiar-contrasena',
    fr: '/compte/changer-mot-de-passe',
    zh: '/账户/更改密码',
  },
  StripePayoutPage: {
    en: '/account/payments',
    es: '/cuenta/pagos',
    fr: '/compte/paiements',
    zh: '/账户/支付',
  },
  StripePayoutOnboardingPage: {
    en: '/account/payments/:returnURLType',
    es: '/cuenta/pagos/:returnURLType',
    fr: '/compte/paiements/:returnURLType',
    zh: '/账户/支付/:returnURLType',
  },
  PaymentMethodsPage: {
    en: '/account/payment-methods',
    es: '/cuenta/metodos-pago',
    fr: '/compte/modes-paiement',
    zh: '/账户/支付方式',
  },
  TermsOfServicePage: {
    en: '/terms-of-service',
    es: '/terminos-de-servicio',
    fr: '/conditions-de-service',
    zh: '/服务条款',
  },
  PrivacyPolicyPage: {
    en: '/privacy-policy',
    es: '/politica-de-privacidad',
    fr: '/politique-de-confidentialite',
    zh: '/隐私政策',
  },
  Styleguide: {
    en: '/styleguide',
    es: '/guia-de-estilo',
    fr: '/guide-de-style',
    zh: '/风格指南',
  },
  StyleguideGroup: {
    en: '/styleguide/g/:group',
    es: '/guia-de-estilo/g/:group',
    fr: '/guide-de-style/g/:group',
    zh: '/风格指南/g/:group',
  },
  StyleguideComponent: {
    en: '/styleguide/c/:component',
    es: '/guia-de-estilo/c/:component',
    fr: '/guide-de-style/c/:component',
    zh: '/风格指南/c/:component',
  },
  StyleguideComponentExample: {
    en: '/styleguide/c/:component/:example',
    es: '/guia-de-estilo/c/:component/:example',
    fr: '/guide-de-style/c/:component/:example',
    zh: '/风格指南/c/:component/:example',
  },
  StyleguideComponentExampleRaw: {
    en: '/styleguide/c/:component/:example/raw',
    es: '/guia-de-estilo/c/:component/:example/raw',
    fr: '/guide-de-style/c/:component/:example/raw',
    zh: '/风格指南/c/:component/:example/raw',
  },
  NotFoundPage: {
    en: '/notfound',
    es: '/no-encontrado',
    fr: '/non-trouve',
    zh: '/未找到',
  },
  PasswordResetPage: {
    en: '/reset-password',
    es: '/restablecer-contrasena',
    fr: '/reinitialiser-mot-de-passe',
    zh: '/重置密码',
  },
  EmailVerificationPage: {
    en: '/verify-email',
    es: '/verificar-correo',
    fr: '/verifier-email',
    zh: '/验证邮箱',
  },
  PreviewResolverPage: {
    en: '/preview',
    es: '/vista-previa',
    fr: '/apercu',
    zh: '/预览',
  },
  CreateAIListing: {
    en: '/rentanything-wizard/new',
    es: '/asistente-alquiler/nuevo',
    fr: '/assistant-location/nouveau',
    zh: '/租赁向导/新建',
  },
  RentAnythingWizardPage: {
    en: '/rentanything-wizard/listing/:action/:step/:listingToken?',
    es: '/asistente-alquiler/alquiler/:action/:step/:listingToken?',
    fr: '/assistant-location/location/:action/:step/:listingToken?',
    zh: '/租赁向导/租赁/:action/:step/:listingToken?',
  },
  PublishedListingPage: {
    en: '/published-listing/:slug/:listingId',
    es: '/listado-publicado/:slug/:listingId',
    fr: '/annonce-publiee/:slug/:listingId',
    zh: '/已发布列表/:slug/:listingId',
  },
  BlogPreviewPage: {
    en: '/blog-preview/:listingId',
    es: '/vista-previa-blog/:listingId',
    fr: '/apercu-blog/:listingId',
    zh: '/博客预览/:listingId',
  },
  AIUpdateListingPageRedirect: {
    en: '/ai/update-listing/:listingId',
    es: '/ai/actualizar-listado/:listingId',
    fr: '/ai/mettre-a-jour-annonce/:listingId',
    zh: '/ai/更新列表/:listingId',
  },
  AIUpdateListingPage: {
    en: '/ai/update-listing/:listingId/:tab',
    es: '/ai/actualizar-listado/:listingId/:tab',
    fr: '/ai/mettre-a-jour-annonce/:listingId/:tab',
    zh: '/ai/更新列表/:listingId/:tab',
  },
  BlogContentPage: {
    en: '/blog-content/:blogId/:listingId',
    es: '/contenido-blog/:blogId/:listingId',
    fr: '/contenu-blog/:blogId/:listingId',
    zh: '/博客内容/:blogId/:listingId',
  },
  StoreBlogContentPage: {
    en: '/rental-owner/:userId/blog/:slug',
    es: '/propietario-alquiler/:userId/blog/:slug',
    fr: '/proprietaire-location/:userId/blog/:slug',
    zh: '/租赁业主/:userId/博客/:slug',
  },
  DeleteAccountPage: {
    en: '/delete-account',
    es: '/eliminar-cuenta',
    fr: '/supprimer-compte',
    zh: '/删除账户',
  },
  StoreSettingsPage: {
    en: '/store-settings',
    es: '/tienda/configuracion',
    fr: '/magasin/parametres',
    zh: '/商店设置',
  },
  StoreBasicInformationPage: {
    en: '/store-settings/basic-information',
    es: '/tienda/informacion-basica',
    fr: '/magasin/informations-de-base',
    zh: '/商店设置/基本信息',
  },
  StoreCategoriesPage: {
    en: '/store-settings/categories',
    es: '/tienda/categorias',
    fr: '/magasin/categories',
    zh: '/商店设置/分类',
  },
  TrustVerificationPage: {
    en: '/store-settings/trust-and-verification',
    es: '/tienda/confianza-y-verificacion',
    fr: '/magasin/confiance-et-verification',
    zh: '/商店设置/信任与验证',
  },
  DefaultSettingsPage: {
    en: '/store-settings/defaults',
    es: '/tienda/valores-predeterminados',
    fr: '/magasin/parametres-par-defaut',
    zh: '/商店设置/默认设置',
  },
  StoreQRCodePage: {
    en: '/store-settings/qr-code',
    es: '/tienda/codigo-qr',
    fr: '/magasin/code-qr',
    zh: '/商店设置/二维码',
  },
};

// Fixed (non-param) prefix of a translated route path, e.g.
// '/rentals/:slug/:id' -> '/rentals/'. The trailing slash is kept
// deliberately: without it, a short prefix like '/l' (from a route path
// like '/l/:slug/:id') would also match unrelated paths such as '/login'.
const staticPathPrefix = path => {
  const paramIndex = path.indexOf('/:');
  return paramIndex === -1 ? path : path.slice(0, paramIndex + 1);
};

// All locale variants of the fixed path prefixes for the given route
// names, e.g. ['SearchPage'] -> ['/search', '/recherche', '/busqueda', '/搜索'].
// Routes with no TRANSLATED_PATHS entry are skipped (callers needing an
// English fallback should consult getTranslatedPath instead).
const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'zh'];
const pathPrefixesForRoutes = routeNames => {
  const prefixes = [];
  routeNames.forEach(routeName => {
    const translations = TRANSLATED_PATHS[routeName];
    if (!translations) return;
    SUPPORTED_LANGUAGES.forEach(lang => {
      const translatedPath = translations[lang];
      if (translatedPath) {
        prefixes.push(staticPathPrefix(translatedPath));
      }
    });
  });
  return Array.from(new Set(prefixes));
};

/**
 * Every address the application actually serves for one route, one per supported language.
 *
 * This reproduces what routeConfiguration.js does when it builds the real routes: the English
 * address as given, and every other language behind its own prefix. The prefix is added
 * whether or not the path itself is translated, so a route with no entry in the table above
 * is still served at /fr/<english path> and the rest.
 *
 * Anything that has to cover a route on the server, rather than only recognise it, needs all
 * of these and not just the English one. Mounting server middleware on the English address
 * alone leaves the other three languages uncovered, which is exactly how the admin console
 * was reachable without passing its gate before this existed.
 *
 * @param {string} routeName the route's name
 * @param {string} fallbackPath the path to use for a language with no entry, which is the
 *   route's own fallbackPath in routeConfiguration.js
 * @returns {string[]} one path per supported language, English first
 */
const localisedPathsForRoute = (routeName, fallbackPath) => {
  const translations = TRANSLATED_PATHS[routeName] || {};
  return SUPPORTED_LANGUAGES.map(lang => {
    const translatedPath = translations[lang] || fallbackPath;
    return lang === 'en' ? translatedPath : `/${lang}${translatedPath}`;
  });
};

module.exports = {
  TRANSLATED_PATHS,
  SUPPORTED_LANGUAGES,
  staticPathPrefix,
  pathPrefixesForRoutes,
  localisedPathsForRoute,
};
